// UiScalePreference — ClassicLayout 左サイドバー / ChatPanel の個人 UI スケール
// 設定を localStorage に永続化する infrastructure 層ユーティリティ.
//
// (consult 2026-05-17 確定方針、2026-05-21 で 3→5 段階に拡張 + iPad 自動 xsmall default、
//  2026-05-27 で 5→6 段階に拡張 = xxsmall=0.72 を edge-add、
//  2026-05-31 で「チャット欄の大きさ」設定そのものを撤去 = chat-scale-cleanup
//  feature branch、alpha-feedback-2026-05-31.md §3 ID=E.
//  「チャット欄の大きさ」は既存 splitter (= chatRowPx / memberColPx の drag) と
//  機能重複していたため削除し、代わりに「チャット**文字**の大きさ」軸を
//  `chatFontScalePreference.ts` で別実装する. UiScale enum は 6 段固定のままで、
//  サイドバー専用に縮小)
//
// Design:
// - **対象**: ClassicLayout の **左カラム wrapper** の CSS `zoom` プロパティ.
//   要素サイズ + 文字 + padding + gap を一括で拡縮する目的なので transform:
//   scale ではなく zoom を使う (= レイアウト box が実サイズに追従、隣接要素を
//   押し退けるので tooltip / popup の座標計算が壊れない).
// - **6 段階**: xxsmall=0.72 / xsmall=0.85 / small=1.0 / medium=1.1 (default) /
//   large=1.25 / xlarge=1.4. 既存ユーザは何もしなくても medium で見える.
//   - `xxsmall=0.72` (2026-05-27 追加) は低解像度・縦が短い端末で左サイドバーの
//     全項目を入れきる最終手段. **2026-08-12 に iPad 自動 default へ昇格**
//     (旧: 「タップ領域が削れるので手動選択のみ」= 2026-08-01 のグリップ帯で
//     必要高が増え縦向きでも入らなくなったため反転. `IPAD_DEFAULT_UI_SCALE`
//     の doc 参照).
//   - `xsmall=0.85` は iPad landscape 等、縦が短い端末で左サイドバーが
//     縦方向に入りきらないケース向け (= 2026-05-21〜2026-08-12 の iPad 自動
//     default. 現在は手動選択の段).
//   - `xlarge=1.4` は 4K / 大型タブレット / 視認性重視 (老眼・弱視) 向け
//     (= サイドバーで実用な上限).
// - **scope**: ユーザー端末ローカル, サーバ同期なし. protocol 変更なし.
//   キーは左サイドバー 1 本のみ (= 2026-05-31 chat キー使用 UI を撤去).
// - **不正値 / 不在時の挙動**: 文字列保存 (`'xxsmall' | 'xsmall' | 'small' |
//   'medium' | 'large' | 'xlarge'`). parse 失敗 or 不在は default 扱い.
//   例外を throw しない.
// - **iPad-like 端末の自動 default 切替** (2026-05-21):
//   - `getSidebar()` の **localStorage 不在時の default のみ** を、iPad / iPhone /
//     iPadOS 偽装 macOS で `'xsmall'` に切り替える (= 縦の入りきらない問題への
//     初期見え方の救済). 一度ユーザが選択して localStorage に書いた値は **絶対に
//     上書きしない** (= 検知より stored 値が優先).
//   - 検知ロジックは `detectIpadLike` option で test 注入可能.
// - **migration**: 既存 localStorage 値 (`'xsmall' / 'small' / 'medium' /
//   'large' / 'xlarge' / 'xxsmall'`) は key も zoom 値も一切変更せず読み戻せる.
//   `oekaki:ui-scale-chat` キーは 2026-05-31 chat-scale-cleanup で読み取らなく
//   なるが orphan として残るだけで害なし (= 削除コードは書かない).
//
// Tests: uiScalePreference.test.ts (storage-injection style、AdminTokenStore.ts
// と同流儀の StorageLike 抽象).
//
// 関連:
// - `presentation/UserPreferencesModal.tsx` — <select> プルダウン. サイドバー
//   のみ 6 段で options 配列を出す (チャット欄サイズ select は 2026-05-31 撤去).
// - `presentation/layouts/classic/ClassicLayout.tsx` — 左カラム wrapper の
//   inline style に `uiScaleToCssZoom(scale)` を当てる.
// - `infrastructure/chatFontScalePreference.ts` — 2026-05-31 新規追加された
//   「チャット文字サイズ」軸. 行高 / 列幅は変えずに log + input の文字だけを
//   em 倍率で拡大する独立軸.

/** 個人 UI スケール段階. 文字列で localStorage に保存される. 6 段固定. */
export type UiScale = 'xxsmall' | 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';

/** デフォルト値 (= medium). 既存ユーザは何もしなくても medium で見える. */
export const DEFAULT_UI_SCALE: UiScale = 'medium';

/**
 * iPad-like 端末での **localStorage 不在時 default** (2026-08-12 に `xsmall` から
 * 変更).
 *
 * 【経緯】2026-05-21 に iPad 自動 default = `xsmall`(0.85) を導入し、2026-05-27 の
 * `xxsmall`(0.72) edge-add 時には「自動 default に使うとタップ領域が削れる」ため
 * **手動選択のみの段**と決めていた。この判断を 2026-08-12 に反転する:
 *
 * - 2026-08-01 (v0.6.0-beta1) の常設グリップ帯 (「ツール」= detach の掴み所) で
 *   サイドバーの必要高が **論理 ~28px (py-1 の帯 + gap-2) 増えた**。
 * - その結果、ユーザー報告 (2026-08-09 = iPad 横向きで見切れ) に加えて
 *   **縦向きでも入りきらない**ことが実機で確認された (作者 iPad、2026-08-12)。
 * - `xsmall`→`xxsmall` は全体が 15% 縮む (概算 560px なら 476px→403px = ~73px 減)
 *   ので、帯の増分 ~28px を十分に取り返せる。
 * - タップ領域が 15% 縮むトレードオフは**承知の上**。「押しにくい」より
 *   「そもそも画面に出ていない・触れない」方が明確に悪い、という判断。
 */
export const IPAD_DEFAULT_UI_SCALE: UiScale = 'xxsmall';

/**
 * 実測で「入りきらない」と分かったときに緊急退避する段 (= 最小段).
 * `rescueSidebarScale` が使う。
 */
export const RESCUE_UI_SCALE: UiScale = 'xxsmall';

/**
 * **入室時サイズオーバー救済** (2026-08-12、作者判断): サイドバーが実測で縦に
 * 入りきらない iPad-like 端末では、**localStorage の保存値を無視して**最小段
 * (`xxsmall`) を当てる。
 *
 * 【なぜ default 変更だけでは足りないか】default は「localStorage に値が無い」
 * ユーザーにしか効かない。ところが **α/β 期に iPad で触ったユーザーは、既に
 * 入りきらないスケールを localStorage に持っている可能性が高い** (= フィード
 * バックをくれた層はまさにそれ)。彼らは default 変更では一切救われない。
 *
 * 【本モジュールの既存ルールとの関係】「一度ユーザが選択して localStorage に
 * 書いた値は絶対に上書きしない」という 2026-05-21 の原則を、**表示の実効値に
 * 限って**緩める。ただし:
 * - **localStorage は書き換えない** (= 保存値は保持する)。画面が広い端末に
 *   移ったり回転で収まったりすれば、本人の選んだ値がそのまま復活する。
 * - **下げる方向にしか動かさない** (既に xxsmall 以下なら何もしない)。
 * - **iPad-like 端末に限る**。デスクトップは B-1 (2026-08-12) で入れた
 *   サイドバーの縦スクロールで到達できるうえ、ウィンドウを広げれば済むので、
 *   勝手に 0.72 へ倒す方が害が大きい。
 *
 * 純関数。実測 (overflow しているか) は呼び出し側 (ClassicLayout) が
 * `scrollHeight > clientHeight` で採る。
 *
 * @param current   現在の実効スケール (= stored 値 or default)
 * @param overflows 実測で縦に入りきっていないか
 * @param isIpadLike iPad-like 端末か
 * @returns 適用すべきスケール (変更不要なら `current` をそのまま返す)
 */
export const rescueSidebarScale = (
  current: UiScale,
  overflows: boolean,
  isIpadLike: boolean,
): UiScale => {
  if (!overflows) return current;
  if (!isIpadLike) return current;
  // 既に最小段なら動かさない (= これ以上下げる先が無い、無駄な再 render も避ける).
  if (current === RESCUE_UI_SCALE) return current;
  return RESCUE_UI_SCALE;
};

/** localStorage キー: 左サイドバー. */
export const SIDEBAR_SCALE_KEY = 'oekaki:ui-scale-sidebar';
/**
 * localStorage キー: bottom row chat + member. 2026-05-31 chat-scale-cleanup
 * で「チャット欄の大きさ」設定 UI を撤去したため、本キーは新規書き込みされない.
 * 既存ユーザの localStorage 値は orphan として残るが、読み出し経路 (`getChat`)
 * も同タイミングで削除済なので動作には影響しない (= 削除コードは書かない、
 * 害なし).
 *
 * 名前は外部観察可能な「ユーザ data」なので test での文字列固定継続のため
 * export を維持する (= 既存テストの shape を保つ目的).
 */
export const CHAT_SCALE_KEY = 'oekaki:ui-scale-chat';

/**
 * UiScale → CSS `zoom` 数値. CSS の `zoom` プロパティは number / percentage を
 * 受けるが、ここでは float 倍率を返す (React inline style は数値 → そのまま CSS).
 *
 * 値は consult 2026-05-17 で中央 3 値 (小=1.0 / 中=1.1 / 大=1.25) を確定し、
 * 2026-05-21 で両端 2 値 (極小=0.85 / 特大=1.4) を追加、2026-05-27 で最小段
 * (xxsmall=0.72) を edge-add. 2026-05-31 chat-scale-cleanup で上振れ 4 段
 * (xxlarge / xxxlarge / huge / gigantic) を撤去して 6 段に戻した.
 */
export const uiScaleToCssZoom = (scale: UiScale): number => {
  switch (scale) {
    case 'xxsmall':
      return 0.72;
    case 'xsmall':
      return 0.85;
    case 'small':
      return 1.0;
    case 'medium':
      return 1.1;
    case 'large':
      return 1.25;
    case 'xlarge':
      return 1.4;
  }
};

/**
 * 文字列が有効な UiScale か判定する type guard. unknown / 不正値の defensive
 * narrowing 用.
 */
export const isUiScale = (v: unknown): v is UiScale =>
  v === 'xxsmall' ||
  v === 'xsmall' ||
  v === 'small' ||
  v === 'medium' ||
  v === 'large' ||
  v === 'xlarge';

/**
 * Minimal Storage shape this module uses (AdminTokenStore / AnonUidStore と同流儀).
 * `globalThis.localStorage` matches; tests pass a mock with the same surface.
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface UiScalePreferenceStore {
  /** 左サイドバー scale を読み出し. 不在 / 不正 / storage 不可 → `DEFAULT_UI_SCALE`. */
  getSidebar(): UiScale;
  /** 左サイドバー scale を保存. storage 不可なら no-op (例外を throw しない). */
  setSidebar(scale: UiScale): void;
  /** sidebar キー (+ orphan chat キー) を削除. テスト / 設定リセット用. storage 不可なら no-op. */
  clear(): void;
}

/**
 * `globalThis.localStorage` をプローブして使用可能なら返す, さもなくば `null`.
 * AdminTokenStore.ts の probe を流用 (sentinel write + readback で Safari
 * private browsing / QuotaExceeded を弾く).
 */
const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:ui-scale__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreateUiScalePreferenceStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op
   * path (= getters always return DEFAULT_UI_SCALE, setters silently drop).
   *
   * テストでは小さな `Map` ベースの fake を渡す.
   */
  storage?: StorageLike | null;

  /**
   * iPad-like 端末判定の override. unit test では `() => true / false` を
   * 直接渡して branch を検証する. 省略時は `defaultDetectIpadLike` (= UA +
   * `maxTouchPoints` ヒューリスティック) を使う.
   *
   * 戻り値が `true` のとき、**sidebar の localStorage 不在時 default のみ**
   * `'xsmall'` に切替わる (= setSidebar 等は不変). 一度書かれた値が再読み込み
   * 時に上書きされることは無い.
   */
  detectIpadLike?: () => boolean;
}

/**
 * iPad / iPhone / iPadOS 偽装 macOS の検知 (default 実装).
 *
 * - iPadOS 12 以前 / iPhone: UA に `iPad` / `iPhone` / `iPod` が出る
 * - iPadOS 13+: macOS Safari に偽装するので UA は `Macintosh` だが
 *   `navigator.maxTouchPoints` が 1 以上 (= 通常の macOS は 0)
 *
 * SSR / node test 環境では `navigator` 自体が無いので `false` を返す.
 *
 * 2026-08-12: `rescueSidebarScale` の呼び出し側 (ClassicLayout) が同じ判定を
 * 二重実装しないよう export した (= iPad 判定のソースを 1 本に保つ).
 */
export const defaultDetectIpadLike = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent ?? '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ workaround: `navigator.platform` は deprecated だが iPad 判定では
  // まだ実用的. 触らせる前に optional chaining で defensive にとっておく.
  const platform = (navigator as { platform?: string }).platform ?? '';
  const touchPoints = (navigator as { maxTouchPoints?: number }).maxTouchPoints ?? 0;
  if (platform === 'MacIntel' && touchPoints > 1) return true;
  return false;
};

/** 単一 key を読み出し → UiScale に narrow. 失敗時 caller 指定の fallback. */
const readUiScale = (storage: StorageLike, key: string, fallback: UiScale): UiScale => {
  try {
    const raw = storage.getItem(key);
    if (raw === null) return fallback;
    return isUiScale(raw) ? raw : fallback;
  } catch {
    return fallback;
  }
};

/** 単一 key に書き込み. 失敗時 silently drop. */
const writeUiScale = (storage: StorageLike, key: string, scale: UiScale): void => {
  try {
    storage.setItem(key, scale);
  } catch {
    // QuotaExceeded / SecurityError 等 — 永続化できないだけで動作は継続させる.
  }
};

/**
 * `UiScalePreferenceStore` のインスタンスを構築. storage が unavailable な
 * 環境 (SSR / Safari private browsing 厳格モード) でも getters は default を
 * 返すだけで、setters は no-op (= 例外で UI が壊れることはない).
 */
export const createUiScalePreferenceStore = (
  opts: CreateUiScalePreferenceStoreOptions = {},
): UiScalePreferenceStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  // iPad-like 端末 (= iPad / iPhone / iPadOS 偽装 macOS) は sidebar の default を
  // xsmall に倒す. 検知は store 構築時 1 回だけ実施 (= 端末特性なので runtime で
  // 切り替わらない). localStorage に既存値があれば検知結果より stored 値が優先.
  const detectIpadLike = opts.detectIpadLike ?? defaultDetectIpadLike;
  const sidebarDefault: UiScale = detectIpadLike() ? IPAD_DEFAULT_UI_SCALE : DEFAULT_UI_SCALE;

  return {
    getSidebar() {
      if (storage === null) return sidebarDefault;
      return readUiScale(storage, SIDEBAR_SCALE_KEY, sidebarDefault);
    },
    setSidebar(scale) {
      if (storage === null) return;
      writeUiScale(storage, SIDEBAR_SCALE_KEY, scale);
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(SIDEBAR_SCALE_KEY);
        // chat キーは 2026-05-31 cleanup で UI 撤去済だが、過去の書き込みが
        // 残っている可能性があるため clear() では一緒に掃除する.
        storage.removeItem(CHAT_SCALE_KEY);
      } catch {
        // ignore
      }
    },
  };
};
